// utils/toolUtilsEnhanced.js
// Enhanced drawing utilities for AI Maths Tutor – refactor v3
/* eslint-disable no-console */

import * as mathjs from "mathjs";
import Chart from "chart.js/auto";
import Konva from "konva";
// import "chartjs-chart-box-and-violin-plot";   // uncomment if you need box/violin plots

/* ─────────── CONSTANTS ─────────── */
export const PALETTE = [
  "#EF5350", "#42A5F5", "#FFCA28", "#66BB6A",
  "#AB47BC", "#26C6DA", "#FFA726", "#78909C",
];
const FONT_FAMILY          = "Inter, Helvetica, Arial, sans-serif";
const DEFAULT_CANVAS_WIDTH = 500;
const DEFAULT_CANVAS_HEIGHT= 400;
const DEFAULT_S3_UPLOAD    = true;
const PADDING = 40; // Add padding constant

/* ─────────── HELPERS ─────────── */
export const makeLinearMapper =
  ([d0, d1], [r0, r1]) => x => r0 + ((x - d0) * (r1 - r0)) / (d1 - d0);

/** Converts a "math-unit" dimension to pixels.
 *  • If val ≤ 10 treat as units (10 units ≈ 70 % of canvas).  
 *  • Otherwise assume it's already in px.
 */
function toPixels(val, canvasDim, margin = 40) {
  if (val > 10) return val;                     // already px
  const maxPx = (canvasDim - margin) / 2;       // leave margins
  return (val / 10) * maxPx;
}

function createStage(params,
                     w = DEFAULT_CANVAS_WIDTH,
                     h = DEFAULT_CANVAS_HEIGHT) {
  const container = document.createElement("div");
  Object.assign(container.style, {
    position: "absolute", left: "-9999px", opacity: "0",
  });
  document.body.appendChild(container);

  const stage = new Konva.Stage({
    container,
    width : params.canvas_width  || w,
    height: params.canvas_height || h,
    listening: false,
  });
  const layer = new Konva.Layer();
  stage.add(layer);
  return { stage, layer, container, width: stage.width(), height: stage.height() };
}
const cleanupStage = (st, el) => { st?.destroy(); el?.remove(); };
const cleanupChart = (ch, cv) => { ch?.destroy(); cv?.remove(); };

// Helper function for drawing text with options
function drawText(ctx, text, x, y, options = {}) {
  const { 
    textAlign = "center", 
    textBaseline = "middle", 
    font = "14px " + FONT_FAMILY,
    fillStyle = "#212121"
  } = options;
  
  ctx.save();
  ctx.textAlign = textAlign;
  ctx.textBaseline = textBaseline;
  ctx.font = font;
  ctx.fillStyle = fillStyle;
  ctx.fillText(text, x, y);
  ctx.restore();
}

async function getImg(instance, key, type, ctx, def = DEFAULT_S3_UPLOAD) {
  let dataUrl;
  
  if (instance instanceof Konva.Stage) {
    dataUrl = instance.toDataURL({ mimeType: "image/png", quality: 0.92 });
  } else if (instance && typeof instance.toBase64Image === 'function') {
    dataUrl = instance.toBase64Image();
  } else if (instance instanceof HTMLCanvasElement) {
    dataUrl = instance.toDataURL("image/png");
  } else {
    throw new Error("Unknown instance type for image generation");
  }

  if (!(ctx?.sessionId && ctx?.userId) || !(ctx.saveToS3 ?? def)) return dataUrl;

  try {
    const { uploadDiagramToS3 } = await import("./S3Utils");
    return await uploadDiagramToS3(dataUrl, key, type, ctx);
  } catch (e) {
    console.warn("S3 upload failed – using dataURL", e);
    return dataUrl;
  }
}

/* ───────────────────────── GRAPHING ───────────────────────── */
function _plotCartesian(exprRaw, opt, diagramType) {
  const xRange = opt.xRange ?? [-10, 10];
  const canvas = document.createElement("canvas");
  canvas.width  = opt.canvas_width  || 600;
  canvas.height = opt.canvas_height || 450;
  document.body.appendChild(canvas);
  const ctx2d = canvas.getContext("2d");

  let chart;
  try {
    const expression = exprRaw.replace(/^(y|f\(x\))\s*=\s*/, "");
    const f = mathjs.compile(expression);

    const N = 300;
    const step = (xRange[1] - xRange[0]) / N;
    const points = [];
    for (let i = 0; i <= N; ++i) {
      const x = xRange[0] + i * step;
      const y = f.evaluate({ x });
      if (Number.isFinite(y)) points.push({ x, y });
    }
    if (!points.length) throw new Error("No valid points produced");

    const ys  = points.map(p => p.y);
    const yLo = Math.min(...ys), yHi = Math.max(...ys);
    const pad = Math.max((yHi - yLo) * 0.1, 1);          // safe even if flat

    chart = new Chart(ctx2d, {
      type: "line",
      data: {
        datasets: [{ data: points, borderColor: PALETTE[0],
                     pointRadius: 0, tension: 0.15,
                     label: opt.title || "f(x)" }],
      },
      options: {
        responsive: false,
        plugins: { title: { display: true, text: opt.title || `Graph of ${exprRaw}`,
                            font: { size: 18 } },
                   legend:{ display:false } },
        scales: { x:{ type:"linear", min:xRange[0], max:xRange[1] },
                  y:{ min:yLo - pad, max:yHi + pad } },
        animation:false,
      },
    });

    return getImg(chart, expression.replace(/[^a-z0-9]/gi,"_"), diagramType, opt)
           .then(url => ({ success:true, imageUrl:url,
                           caption: opt.title || `Graph of ${exprRaw}` }));
  } catch (err) {
    return { success:false, error:err.message };
  } finally { cleanupChart(chart, canvas); }
}
export const plotGraph = (expr, opts={}) => _plotCartesian(expr, opts, "function_graph");

/* ───────────── GEOMETRY – shape-specific ───────────── */

// 1. Circle
export const draw_circle = async (args) => {
  const canvas = document.createElement("canvas");
  canvas.width = args.canvas_width || DEFAULT_CANVAS_WIDTH;
  canvas.height = args.canvas_height || DEFAULT_CANVAS_HEIGHT;
  const ctx = canvas.getContext("2d");

  try {
    const radiusVal = args.radius ?? 1; // Default radius if not provided
    const title = args.title || `Circle (Radius: ${radiusVal})`;

    // --- Scaling ---
    // Determine max possible radius based on canvas size and padding
    const maxDim = Math.min(canvas.width, canvas.height);
    const drawableRadius = (maxDim / 2) - PADDING;
    const PIXELS_PER_UNIT = Math.min(50, drawableRadius / (radiusVal || 1)); // Ensure it fits
    const rPx = radiusVal * PIXELS_PER_UNIT;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    ctx.strokeStyle = PALETTE[0];
    ctx.lineWidth = 2;

    // Draw circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, rPx, 0, 2 * Math.PI);
    ctx.stroke();

    // Center dot
    ctx.beginPath();
    ctx.arc(centerX, centerY, 3, 0, 2 * Math.PI);
    ctx.fillStyle = PALETTE[1];
    ctx.fill();
    if (args.center_label) {
      drawText(ctx, args.center_label, centerX, centerY - 10, { textAlign: "center", textBaseline: "bottom"});
    }

    // Radius line
    if (args.show_radius_line) {
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(centerX + rPx, centerY);
      ctx.stroke();
      if (args.radius_label) {
        drawText(ctx, args.radius_label, centerX + rPx / 2, centerY - 5, {textBaseline: "bottom"});
      }
    }

    // Diameter line
    if (args.show_diameter_line) {
      ctx.beginPath();
      ctx.moveTo(centerX - rPx, centerY);
      ctx.lineTo(centerX + rPx, centerY);
      // Make it slightly different if both radius and diameter are shown
      if (args.show_radius_line) {
        ctx.strokeStyle = PALETTE[2];
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.setLineDash([]); // Reset
        ctx.strokeStyle = PALETTE[0]; // Reset
      } else {
        ctx.stroke();
      }
      if (args.diameter_label) {
        drawText(ctx, args.diameter_label, centerX, centerY + 5, {textBaseline: "top"});
      }
    }
    
    if (args.circumference_label) {
        drawText(ctx, args.circumference_label, centerX + rPx * Math.cos(Math.PI/4) + 5, centerY - rPx * Math.sin(Math.PI/4) - 5, {textAlign:"left"});
    }

    // Title
    drawText(ctx, title, canvas.width / 2, PADDING / 2, { font: "18px " + FONT_FAMILY });

    const imageUrl = await getImg(canvas, `circle_${radiusVal}`, "circle", args);
    return { success: true, imageUrl, caption: title };

  } catch (err) {
    console.error("Error drawing circle:", err);
    return { success: false, error: err.message };
  } finally {
    cleanupChart(null, canvas);
  }
};

// 2. Triangle
export const draw_triangle = async (args) => {
  const canvas = document.createElement("canvas");
  canvas.width = args.canvas_width || DEFAULT_CANVAS_WIDTH;
  canvas.height = args.canvas_height || DEFAULT_CANVAS_HEIGHT;
  const ctx = canvas.getContext("2d");

  try {
    let {
      base, height, side_lengths, angle_measures,
      vertex_A_label, vertex_B_label, vertex_C_label,
      side_AB_label, side_BC_label, side_CA_label,
      angle_A_label, angle_B_label, angle_C_label,
      show_altitude_from, title
    } = args;

    // Default to equilateral if not enough info
    const isEquilateralDefault = (!base && !height && !side_lengths && !angle_measures);
    base = base ?? (isEquilateralDefault ? 5 : undefined); // Default base if equilateral

    let pointsModel = []; // [ {x, y, name:"A"}, ... ] in model coordinates

    // --- Determine triangle vertices in model coordinates ---
    // Priority: side_lengths, then base & height (isosceles), then equilateral
    if (side_lengths) {
        let sA, sB, sC; // side_lengths opposite to A, B, C (BC, CA, AB)
        if (Array.isArray(side_lengths)) {
            [sA, sB, sC] = side_lengths;
        } else if (typeof side_lengths === 'object') { // {AB, BC, CA}
            sC = side_lengths.AB; sA = side_lengths.BC; sB = side_lengths.CA;
        }
        if (!sA || !sB || !sC) throw new Error("Invalid side_lengths provided.");

        // Check triangle inequality
        if (sA + sB <= sC || sA + sC <= sB || sB + sC <= sA) {
            throw new Error("Triangle inequality violated by side_lengths.");
        }

        // Place B at origin, C on x-axis
        pointsModel.push({x: 0, y: 0, name: "B"}); // Vertex B
        pointsModel.push({x: sA, y: 0, name: "C"}); // Vertex C (BC = sA)
        
        // Calculate vertex A using Law of Cosines for angle B or direct formula
        const cosB = (sA*sA + sC*sC - sB*sB) / (2 * sA * sC);
        const sinB = Math.sqrt(1 - cosB*cosB); // sinB is positive for valid triangles
        pointsModel.push({x: sC * cosB, y: sC * sinB, name: "A"});

    } else if (base && height) { // Assume isosceles for drawing if only base and height
        pointsModel.push({ x: -base / 2, y: 0, name: "B" });
        pointsModel.push({ x: base / 2, y: 0, name: "C" });
        pointsModel.push({ x: 0, y: height, name: "A" });
    } else if (base) { // Equilateral
        const side = base;
        height = (Math.sqrt(3) / 2) * side;
        pointsModel.push({ x: -side / 2, y: 0, name: "B" });
        pointsModel.push({ x: side / 2, y: 0, name: "C" });
        pointsModel.push({ x: 0, y: height, name: "A" });
    } else {
         throw new Error("Insufficient parameters to draw a triangle. Provide side_lengths, or base and height, or just base (for equilateral).");
    }
    
    const pA = pointsModel.find(p=>p.name==="A");
    const pB = pointsModel.find(p=>p.name==="B");
    const pC = pointsModel.find(p=>p.name==="C");

    if (!pA || !pB || !pC) throw new Error("Could not determine all triangle vertices.");

    // --- Scaling and Centering ---
    const allX = [pA.x, pB.x, pC.x];
    const allY = [pA.y, pB.y, pC.y];
    const minX = Math.min(...allX), maxX = Math.max(...allX);
    const minY = Math.min(...allY), maxY = Math.max(...allY);
    const modelWidth = maxX - minX;
    const modelHeight = maxY - minY;

    const canvasDrawableWidth = canvas.width - 2 * PADDING;
    const canvasDrawableHeight = canvas.height - 2 * PADDING;

    const scaleX = modelWidth === 0 ? 1 : canvasDrawableWidth / modelWidth;
    const scaleY = modelHeight === 0 ? 1 : canvasDrawableHeight / modelHeight;
    const scale = Math.min(scaleX, scaleY) * 0.9; // Use 90% of available space

    const finalWidth = modelWidth * scale;
    const finalHeight = modelHeight * scale;
    const offsetX = PADDING + (canvasDrawableWidth - finalWidth) / 2;
    const offsetY = PADDING + (canvasDrawableHeight - finalHeight) / 2;

    const transform = (pt) => ({
      x: offsetX + (pt.x - minX) * scale,
      y: offsetY + (maxY - pt.y) * scale, // Flip Y-axis and map from model to canvas
      name: pt.name
    });
    
    const [vA, vB, vC] = [transform(pA), transform(pB), transform(pC)];

    // --- Drawing ---
    ctx.strokeStyle = PALETTE[0];
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(vA.x, vA.y);
    ctx.lineTo(vB.x, vB.y);
    ctx.lineTo(vC.x, vC.y);
    ctx.closePath();
    ctx.stroke();

    // Labels
    if (vertex_A_label) drawText(ctx, vertex_A_label, vA.x, vA.y - 10, {textBaseline:"bottom"}); else drawText(ctx, "A", vA.x, vA.y - 10, {textBaseline:"bottom"});
    if (vertex_B_label) drawText(ctx, vertex_B_label, vB.x - 10, vB.y + 5, {textAlign:"right"}); else drawText(ctx, "B", vB.x - 10, vB.y + 5, {textAlign:"right"});
    if (vertex_C_label) drawText(ctx, vertex_C_label, vC.x + 10, vC.y + 5, {textAlign:"left"}); else drawText(ctx, "C", vC.x + 10, vC.y + 5, {textAlign:"left"});

    const midAB = { x: (vA.x+vB.x)/2, y: (vA.y+vB.y)/2 };
    const midBC = { x: (vB.x+vC.x)/2, y: (vB.y+vC.y)/2 };
    const midCA = { x: (vC.x+vA.x)/2, y: (vC.y+vA.y)/2 };
    if (side_AB_label) drawText(ctx, side_AB_label, midAB.x - 5, midAB.y - 5);
    if (side_BC_label) drawText(ctx, side_BC_label, midBC.x, midBC.y + 10, {textBaseline:"top"});
    if (side_CA_label) drawText(ctx, side_CA_label, midCA.x + 5, midCA.y - 5);

    // TODO: Angle labels (more complex to position nicely)
    // TODO: Altitude from show_altitude_from

    const finalTitle = title || "Triangle";
    drawText(ctx, finalTitle, canvas.width / 2, PADDING / 2, { font: "18px " + FONT_FAMILY });

    const imageUrl = await getImg(canvas, `triangle`, "triangle", args);
    return { success: true, imageUrl, caption: finalTitle };

  } catch (err) {
    console.error("Error drawing triangle:", err);
    return { success: false, error: err.message };
  } finally {
    cleanupChart(null, canvas);
  }
};

// 3. Quadrilateral
export const draw_quadrilateral = async (args) => {
  const canvas = document.createElement("canvas");
  canvas.width = args.canvas_width || DEFAULT_CANVAS_WIDTH;
  canvas.height = args.canvas_height || DEFAULT_CANVAS_HEIGHT;
  const ctx = canvas.getContext("2d");

  try {
    const { 
      type, side_length, base, height, slant, base2, 
      vertex_labels, side_labels, title: userTitle 
    } = args;
    let pointsModel = []; // [{x,y}, {x,y}, {x,y}, {x,y}] in counter-clockwise order from bottom-left

    let shapeName = type;

    switch (type) {
      case "square":
        const s = side_length ?? base ?? 5; // Default side length
        pointsModel = [ {x:0,y:0}, {x:s,y:0}, {x:s,y:s}, {x:0,y:s} ];
        shapeName = "Square";
        break;
      case "rectangle":
        const rBase = base ?? 5;
        const rHeight = height ?? 3;
        pointsModel = [ {x:0,y:0}, {x:rBase,y:0}, {x:rBase,y:rHeight}, {x:0,y:rHeight} ];
        shapeName = "Rectangle";
        break;
      case "parallelogram":
        const pBase = base ?? 5;
        const pHeight = height ?? 3;
        const pSlant = slant ?? 1.5; // Horizontal offset for the top side
        pointsModel = [ {x:0,y:0}, {x:pBase,y:0}, {x:pBase+pSlant,y:pHeight}, {x:pSlant,y:pHeight} ];
        shapeName = "Parallelogram";
        break;
      case "trapezoid": // Assuming isosceles trapezoid if only one base and height
        const tBase1 = base ?? 6;    // Bottom base
        const tBase2 = base2 ?? 4;   // Top base
        const tHeight = height ?? 3;
        const diff = (tBase1 - tBase2) / 2;
        pointsModel = [ {x:0,y:0}, {x:tBase1,y:0}, {x:tBase1-diff,y:tHeight}, {x:diff,y:tHeight} ];
        shapeName = "Trapezoid";
        break;
      default:
        throw new Error(`Unsupported quadrilateral type: ${type}`);
    }
    
    // --- Scaling and Centering (similar to triangle) ---
    const allX = pointsModel.map(p => p.x);
    const allY = pointsModel.map(p => p.y);
    const minX = Math.min(...allX), maxX = Math.max(...allX);
    const minY = Math.min(...allY), maxY = Math.max(...allY);
    const modelWidth = maxX - minX;
    const modelHeight = maxY - minY;

    const canvasDrawableWidth = canvas.width - 2 * PADDING;
    const canvasDrawableHeight = canvas.height - 2 * PADDING;

    const scaleX = modelWidth === 0 ? 1 : canvasDrawableWidth / modelWidth;
    const scaleY = modelHeight === 0 ? 1 : canvasDrawableHeight / modelHeight;
    const scale = Math.min(scaleX, scaleY) * 0.9; 

    const finalWidth = modelWidth * scale;
    const finalHeight = modelHeight * scale;
    const offsetX = PADDING + (canvasDrawableWidth - finalWidth) / 2;
    const offsetY = PADDING + (canvasDrawableHeight - finalHeight) / 2;

    const finalPoints = pointsModel.map(pt => ({
      x: offsetX + (pt.x - minX) * scale,
      y: offsetY + (maxY - pt.y) * scale, // Flip Y-axis
    }));

    // --- Drawing ---
    ctx.strokeStyle = PALETTE[0];
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(finalPoints[0].x, finalPoints[0].y);
    for (let i = 1; i < finalPoints.length; i++) {
      ctx.lineTo(finalPoints[i].x, finalPoints[i].y);
    }
    ctx.closePath();
    ctx.stroke();

    // Vertex Labels (A, B, C, D counter-clockwise from bottom-left if not provided)
    const defaultLabels = ["A", "B", "C", "D"];
    const labels = vertex_labels && vertex_labels.length === 4 ? vertex_labels : defaultLabels;
    const labelOffsets = [ {x:-10,y:10}, {x:10,y:10}, {x:10,y:-10}, {x:-10,y:-10} ]; // BL, BR, TR, TL
    finalPoints.forEach((p, i) => {
      drawText(ctx, labels[i], p.x + labelOffsets[i].x, p.y + labelOffsets[i].y, {
          textAlign: labelOffsets[i].x < 0 ? "right" : "left",
          textBaseline: labelOffsets[i].y < 0 ? "bottom" : "top"
      });
    });

    // Side labels - NEW FEATURE
    if (side_labels && side_labels.length > 0) {
      const sideNames = ["AB", "BC", "CD", "DA"]; // Default side naming convention
      
      for (let i = 0; i < 4; i++) {
        if (side_labels[i]) {
          const currentPoint = finalPoints[i];
          const nextPoint = finalPoints[(i + 1) % 4]; // Wrap around to first point
          
          // Calculate midpoint of the side
          const midX = (currentPoint.x + nextPoint.x) / 2;
          const midY = (currentPoint.y + nextPoint.y) / 2;
          
          // Calculate offset direction (perpendicular to the side, pointing outward)
          const sideVecX = nextPoint.x - currentPoint.x;
          const sideVecY = nextPoint.y - currentPoint.y;
          
          // Perpendicular vector (rotated 90 degrees counterclockwise)
          const perpX = -sideVecY;
          const perpY = sideVecX;
          
          // Normalize and scale
          const perpLength = Math.sqrt(perpX * perpX + perpY * perpY);
          if (perpLength > 0) { // Avoid division by zero
            const SIDE_LABEL_OFFSET = 15;
            const offsetX = (perpX / perpLength) * SIDE_LABEL_OFFSET;
            const offsetY = (perpY / perpLength) * SIDE_LABEL_OFFSET;
            
            // Position label slightly outside the quadrilateral
            const labelX = midX + offsetX;
            const labelY = midY + offsetY;
            
            drawText(ctx, side_labels[i], labelX, labelY, {
              textAlign: "center",
              textBaseline: "middle"
            });
          }
        }
      }
    }
    
    const finalTitle = userTitle || shapeName;
    drawText(ctx, finalTitle, canvas.width / 2, PADDING / 2, { font: "18px " + FONT_FAMILY });

    const imageUrl = await getImg(canvas, `quad_${type}`, "quadrilateral", args);
    return { success: true, imageUrl, caption: finalTitle };

  } catch (err) {
    console.error("Error drawing quadrilateral:", err);
    return { success: false, error: err.message };
  } finally {
    cleanupChart(null, canvas);
  }
};

// 4. Regular Polygon - Enhanced with side labels
export const draw_polygon_regular = async (args) => {
  const canvas = document.createElement("canvas");
  canvas.width = args.canvas_width || DEFAULT_CANVAS_WIDTH;
  canvas.height = args.canvas_height || DEFAULT_CANVAS_HEIGHT;
  const ctx = canvas.getContext("2d");

  try {
    const { num_sides, side_length, center_label, vertex_labels, side_labels, title: userTitle } = args;

    if (num_sides < 3) throw new Error("Polygon must have at least 3 sides.");
    
    const polygonRadiusVal = side_length ?? 3; // This is the radius (center to vertex)

    // --- Scaling ---
    const maxDim = Math.min(canvas.width, canvas.height);
    const drawableRadius = (maxDim / 2) - PADDING;
    const PIXELS_PER_UNIT_RADIUS = Math.min(50, drawableRadius / (polygonRadiusVal || 1));
    const rPx = polygonRadiusVal * PIXELS_PER_UNIT_RADIUS;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    const points = [];
    for (let i = 0; i < num_sides; i++) {
      // Start from top: -Math.PI / 2
      const angle = (i / num_sides) * 2 * Math.PI - Math.PI / 2;
      points.push({
        x: centerX + rPx * Math.cos(angle),
        y: centerY + rPx * Math.sin(angle),
      });
    }

    // --- Drawing ---
    ctx.strokeStyle = PALETTE[0];
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < num_sides; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.closePath();
    ctx.stroke();

    if (center_label) {
      drawText(ctx, center_label, centerX, centerY);
    } else { // Draw a small center dot
      ctx.beginPath();
      ctx.arc(centerX, centerY, 2, 0, 2 * Math.PI);
      ctx.fillStyle = PALETTE[1];
      ctx.fill();
    }

    // Vertex labels
    if (vertex_labels && vertex_labels.length > 0) {
      points.forEach((p, i) => {
        if (vertex_labels[i]) {
          const LBL_OFFSET = 15;
          const angle = (i / num_sides) * 2 * Math.PI - Math.PI / 2;
          const lblX = centerX + (rPx + LBL_OFFSET) * Math.cos(angle);
          const lblY = centerY + (rPx + LBL_OFFSET) * Math.sin(angle);
          drawText(ctx, vertex_labels[i], lblX, lblY);
        }
      });
    }

    // Side labels - NEW FEATURE
    if (side_labels && side_labels.length > 0) {
      for (let i = 0; i < num_sides; i++) {
        if (side_labels[i]) {
          const currentPoint = points[i];
          const nextPoint = points[(i + 1) % num_sides]; // Wrap around to first point
          
          // Calculate midpoint of the side
          const midX = (currentPoint.x + nextPoint.x) / 2;
          const midY = (currentPoint.y + nextPoint.y) / 2;
          
          // Calculate offset direction (perpendicular to the side, pointing outward)
          const sideVecX = nextPoint.x - currentPoint.x;
          const sideVecY = nextPoint.y - currentPoint.y;
          
          // Perpendicular vector (rotated 90 degrees counterclockwise)
          const perpX = -sideVecY;
          const perpY = sideVecX;
          
          // Normalize and scale
          const perpLength = Math.sqrt(perpX * perpX + perpY * perpY);
          const SIDE_LABEL_OFFSET = 15;
          const offsetX = (perpX / perpLength) * SIDE_LABEL_OFFSET;
          const offsetY = (perpY / perpLength) * SIDE_LABEL_OFFSET;
          
          // Position label slightly outside the polygon
          const labelX = midX + offsetX;
          const labelY = midY + offsetY;
          
          drawText(ctx, side_labels[i], labelX, labelY, {
            textAlign: "center",
            textBaseline: "middle"
          });
        }
      }
    }
    
    const finalTitle = userTitle || `${num_sides}-sided Regular Polygon`;
    drawText(ctx, finalTitle, canvas.width / 2, PADDING / 2, { font: "18px " + FONT_FAMILY });
    
    const imageUrl = await getImg(canvas, `polygon_${num_sides}`, "polygon_regular", args);
    return { success: true, imageUrl, caption: finalTitle };

  } catch (err) {
    console.error("Error drawing regular polygon:", err);
    return { success: false, error: err.message };
  } finally {
    cleanupChart(null, canvas);
  }
};

/* ────────────── DATA / STATS ────────────── */
// This will use Chart.js like your plot_graph example
export const draw_data_chart = async (args) => {
  const canvas = document.createElement("canvas");
  canvas.width  = args.canvas_width  || DEFAULT_CANVAS_WIDTH; // Use provided or default
  canvas.height = args.canvas_height || DEFAULT_CANVAS_HEIGHT;
  const ctx2d = canvas.getContext("2d");
  let chart; // Chart.js instance

  try {
    const { 
      chart_type, data_points, 
      histogram_bin_width, best_fit, 
      title, x_axis_label, y_axis_label 
    } = args;

    if (!data_points || data_points.length === 0) {
      throw new Error("No data_points provided for the chart.");
    }

    let chartData = {};
    let chartOptions = {
      responsive: false, // Important for consistent image capture
      plugins: {
        title: { display: !!title, text: title || `Chart: ${chart_type}`, font: { size: 18 } },
        legend: { display: true } 
      },
      scales: {},
      animation: false,
    };

    if (x_axis_label) {
      chartOptions.scales.x = { ...chartOptions.scales.x, title: { display: true, text: x_axis_label } };
    }
    if (y_axis_label) {
      chartOptions.scales.y = { ...chartOptions.scales.y, title: { display: true, text: y_axis_label } };
    }

    let finalChartType = chart_type; // May be adapted (e.g. histogram)
    let datasets = [];
    let labels = [];

    switch (chart_type) {
      case "bar":
      case "pie":
      case "line_discrete": // Chart.js 'line' type can handle discrete points
        if (typeof data_points[0] === 'object' && (data_points[0].hasOwnProperty('label') || data_points[0].hasOwnProperty('x'))) {
            labels = data_points.map(p => p.label || p.x);
            const values = data_points.map(p => p.value || p.y);
            datasets.push({
              label: title || chart_type,
              data: values,
              backgroundColor: PALETTE.slice(0, values.length),
              borderColor: PALETTE[0],
              fill: chart_type === "line_discrete" ? false : undefined, // No fill for line
              tension: chart_type === "line_discrete" ? 0.1 : undefined, // Slight curve for line
            });
        } else if (Array.isArray(data_points) && typeof data_points[0] === 'number') { // Simple array of values
            labels = data_points.map((_, i) => `Data ${i+1}`);
            datasets.push({
              label: title || chart_type,
              data: data_points,
              backgroundColor: PALETTE.slice(0, data_points.length),
              borderColor: PALETTE[0],
              fill: chart_type === "line_discrete" ? false : undefined,
              tension: chart_type === "line_discrete" ? 0.1 : undefined,
            });
        } else {
            throw new Error("Unsupported data_points format for bar/pie/line_discrete. Expect array of objects {label/x, value/y} or array of numbers.");
        }
        if (chart_type === "line_discrete") finalChartType = "line";
        if (chart_type === "pie") chartOptions.plugins.legend.display = true; // Often good for pie
        else chartOptions.plugins.legend.display = false; // Usually not needed for single series bar/line

        break;

      case "scatter":
        // Assuming data_points are [{x: 1, y: 2}, {x: 3, y: 4}, ...]
        if (!(typeof data_points[0] === 'object' && data_points[0].hasOwnProperty('x') && data_points[0].hasOwnProperty('y'))) {
             throw new Error("Scatter plot data_points must be an array of objects like {x: number, y: number}.");
        }
        datasets.push({
          label: title || 'Scatter Plot',
          data: data_points,
          backgroundColor: PALETTE[0],
        });
        chartOptions.scales.x = { ...chartOptions.scales.x, type: 'linear', position: 'bottom' };
        chartOptions.plugins.legend.display = false;

        if (best_fit) {
            // Basic linear regression
            if (data_points.length >= 2) {
                let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
                const n = data_points.length;
                data_points.forEach(p => {
                    sumX += p.x;
                    sumY += p.y;
                    sumXY += p.x * p.y;
                    sumXX += p.x * p.x;
                });
                const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
                const intercept = (sumY - slope * sumX) / n;
                
                if (isFinite(slope) && isFinite(intercept)) {
                    const minX = Math.min(...data_points.map(p=>p.x));
                    const maxX = Math.max(...data_points.map(p=>p.x));
                    datasets.push({
                        label: 'Best Fit Line',
                        data: [{x: minX, y: slope * minX + intercept}, {x: maxX, y: slope * maxX + intercept}],
                        borderColor: PALETTE[1],
                        type: 'line', // Draw this as a line
                        fill: false,
                        pointRadius: 0,
                        tension: 0
                    });
                    chartOptions.plugins.legend.display = true; // Show legend if best fit is there
                } else {
                    console.warn("Could not calculate best fit line (e.g., vertical data).");
                }
            }
        }
        break;
      
      case "histogram_simple":
        // data_points is an array of numbers e.g. [1,2,2,3,3,3,4,5]
        if (!data_points.every(p => typeof p === 'number')) {
            throw new Error("Histogram data_points must be an array of numbers.");
        }
        const binWidth = histogram_bin_width || 1; // Default bin width
        const minVal = Math.min(...data_points);
        const maxVal = Math.max(...data_points);
        
        const bins = {};
        for (let val of data_points) {
          const binStart = Math.floor(val / binWidth) * binWidth;
          const binLabel = `${binStart} to <${binStart + binWidth}`;
          bins[binLabel] = (bins[binLabel] || 0) + 1;
        }

        // Sort bins by start value for correct order on chart
        const sortedBinLabels = Object.keys(bins).sort((a, b) => {
            return parseFloat(a.split(" ")[0]) - parseFloat(b.split(" ")[0]);
        });

        labels = sortedBinLabels;
        const histValues = sortedBinLabels.map(label => bins[label]);

        datasets.push({
          label: title || 'Histogram',
          data: histValues,
          backgroundColor: PALETTE[0],
          barPercentage: 1.0,     // No gap between bars
          categoryPercentage: 1.0 // No gap between bars
        });
        finalChartType = "bar"; // Use bar chart type for histogram
        chartOptions.scales.x = { ...chartOptions.scales.x, grid: { display: false }}; // Often looks cleaner for histograms
        chartOptions.plugins.legend.display = false;
        break;

      case "box":
        // Basic placeholder for box plot
        console.warn("Box plot type requires a Chart.js extension (e.g., chartjs-chart-box-and-violin-plot) or manual drawing. This is a placeholder.");
        if (!data_points.every(p => typeof p === 'number')) {
            throw new Error("Box plot data_points must be an array of numbers.");
        }
        data_points.sort((a, b) => a - b);
        const q1 = data_points[Math.floor(data_points.length / 4)];
        const median = data_points[Math.floor(data_points.length / 2)];
        const q3 = data_points[Math.floor((3 * data_points.length) / 4)];
        labels = ["Q1", "Median", "Q3"];
        datasets.push({
            label: 'Data Distribution (Simplified)',
            data: [q1, median, q3],
            backgroundColor: PALETTE.slice(0,3)
        });
        finalChartType = "bar";
        chartOptions.plugins.legend.display = false;
        break;

      default:
        throw new Error(`Unsupported chart_type: ${chart_type}`);
    }

    chartData.labels = labels;
    chartData.datasets = datasets;

    chart = new Chart(ctx2d, {
      type: finalChartType,
      data: chartData,
      options: chartOptions,
    });

    const imageNameSeed = `${chart_type}_${(title || "chart").replace(/[^a-z0-9]/gi, "_")}`;
    const imageUrl = await getImg(chart, imageNameSeed, chart_type, args);
    return { success: true, imageUrl: imageUrl, caption: title || `Chart: ${chart_type}` };

  } catch (err) {
    console.error(`Error drawing data chart (${args.chart_type}):`, err);
    return { success: false, error: err.message };
  } finally {
    cleanupChart(chart, canvas);
  }
};

/* ───────────────────────── LEGACY KONVA FUNCTIONS (for compatibility) ───────────────────────── */
// Keep old functions for backward compatibility but mark as deprecated
async function drawDataChart(params, ctx) {
  console.warn("drawDataChart is deprecated, use draw_data_chart instead");
  return draw_data_chart(params);
}

function addExtraShape({ type, dimensions, cx, cy, fill, stroke, strokeWidth, layer }) {
  switch (type) {
    case "parallelogram": {
      const a = dimensions.base, h = dimensions.height, s = dimensions.slant;
      const pts=[cx-s-a/2,cy+h/2, cx+a/2-s,cy+h/2, cx+a/2+s,cy-h/2, cx-s+a/2,cy-h/2];
      layer.add(new Konva.Line({ points:pts, closed:true, fill, stroke, strokeWidth }));
      return true;
    }
    case "trapezoid": {
      const {base1:a, base2:b, height:h}=dimensions;
      const pts=[cx-a/2,cy+h/2, cx+a/2,cy+h/2, cx+b/2,cy-h/2, cx-b/2,cy-h/2];
      layer.add(new Konva.Line({ points:pts, closed:true, fill, stroke, strokeWidth }));
      return true;
    }
    default: return false;
  }
}

function getLabelPosition(type, hint, g, dimensions = {}) {
  if (type === "circle") {
    const { cx, cy, r } = g;
    const map = { 
      center: [cx, cy], 
      top: [cx, cy - r - 20], 
      bottom: [cx, cy + r + 20],
      left: [cx - r - 30, cy], 
      right: [cx + r + 20, cy] 
    };
    return map[hint] ? { x: map[hint][0], y: map[hint][1] } : { x: cx + r + 20, y: cy };
  }
  
  if (type === "triangle") {
    const { A, B, C } = g;
    const pos = { 
      A: [A.x - 20, A.y - 15], 
      B: [B.x + 15, B.y - 15], 
      C: [C.x - 15, C.y + 25],
      AB: [(A.x + B.x) / 2, (A.y + B.y) / 2 + 25],
      BC: [(B.x + C.x) / 2 + 20, (B.y + C.y) / 2],
      CA: [(C.x + A.x) / 2 - 30, (C.y + A.y) / 2]
    };
    if (pos[hint]) return { x: pos[hint][0], y: pos[hint][1] };
  }
  
  // Handle quadrilaterals - dimensions here are already in pixels from toPixels()
  if (type === "square") {
    const { cx, cy } = g;
    const { side } = dimensions;
    const halfSide = side / 2;
    
    const vertexPositions = {
      A: [cx - halfSide - 25, cy - halfSide - 15], // top-left
      B: [cx + halfSide + 15, cy - halfSide - 15], // top-right  
      C: [cx + halfSide + 15, cy + halfSide + 25], // bottom-right
      D: [cx - halfSide - 25, cy + halfSide + 25], // bottom-left
    };
    
    if (vertexPositions[hint]) {
      return { x: vertexPositions[hint][0], y: vertexPositions[hint][1] };
    }
  }
  
  if (type === "rectangle") {
    const { cx, cy } = g;
    const { length, width } = dimensions; // These are already in pixels
    const halfLength = length / 2;
    const halfWidth = width / 2;
    
    const vertexPositions = {
      A: [cx - halfLength - 25, cy - halfWidth - 15], // top-left
      B: [cx + halfLength + 15, cy - halfWidth - 15], // top-right  
      C: [cx + halfLength + 15, cy + halfWidth + 25], // bottom-right
      D: [cx - halfLength - 25, cy + halfWidth + 25], // bottom-left
    };
    
    if (vertexPositions[hint]) {
      return { x: vertexPositions[hint][0], y: vertexPositions[hint][1] };
    }
  }
  
  if (type === "parallelogram") {
    const { cx, cy } = g;
    const { base, height, slant } = dimensions; // Already in pixels
    const halfBase = base / 2;
    const halfHeight = height / 2;
    
    const vertexPositions = {
      A: [cx - slant - halfBase - 25, cy + halfHeight + 25], // bottom-left
      B: [cx + halfBase - slant + 15, cy + halfHeight + 25], // bottom-right
      C: [cx + halfBase + slant + 15, cy - halfHeight - 15], // top-right
      D: [cx - slant + halfBase - 25, cy - halfHeight - 15], // top-left
    };
    
    if (vertexPositions[hint]) {
      return { x: vertexPositions[hint][0], y: vertexPositions[hint][1] };
    }
  }
  
  if (type === "trapezoid") {
    const { cx, cy } = g;
    const { base1, base2, height } = dimensions; // Already in pixels
    const halfBase1 = base1 / 2;
    const halfBase2 = base2 / 2;
    const halfHeight = height / 2;
    
    const vertexPositions = {
      A: [cx - halfBase1 - 25, cy + halfHeight + 25], // bottom-left
      B: [cx + halfBase1 + 15, cy + halfHeight + 25], // bottom-right
      C: [cx + halfBase2 + 15, cy - halfHeight - 15], // top-right
      D: [cx - halfBase2 - 25, cy - halfHeight - 15], // top-left
    };
    
    if (vertexPositions[hint]) {
      return { x: vertexPositions[hint][0], y: vertexPositions[hint][1] };
    }
  }
  
  // Add regular polygon positioning
  if (type === "regular_polygon") {
    const { cx, cy } = g;
    if (hint === "center") {
      return { x: cx, y: cy };
    }
    // For vertex positioning, we'd need to calculate vertex positions
    // This would require the radius and number of sides
    const match = hint.match(/vertex_(\d+)/);
    if (match && dimensions.side_length && dimensions.num_sides) {
      const index = parseInt(match[1]);
      const radius = dimensions.side_length; // In your code, this is treated as radius
      const angleStep = (2 * Math.PI) / dimensions.num_sides;
      const angle = index * angleStep - Math.PI / 2; // Start from top
      
      return { 
        x: cx + radius * Math.cos(angle) + 15, 
        y: cy + radius * Math.sin(angle) - 10 
      };
    }
  }
  
  return null;
}

async function drawGeometricShape(params, ctx) {
  const { stage, layer, container, width: w, height: h } = createStage(params);
  try {
    const { shape_type: type, dimensions = {}, labels = [], title } = params;
    const cx = w / 2, cy = h / 2;
    const fill = params.fill_color ?? "rgba(100,181,246,.3)";
    const stroke = params.stroke_color ?? "#0D47A1";
    const strokeWidth = params.stroke_width ?? 2;

    let drawn = false, geom = { cx, cy };

    switch (type) {
      case "circle": {
        const r = toPixels(dimensions.radius ?? 3, Math.min(w, h));
        layer.add(new Konva.Circle({ x: cx, y: cy, radius: r, fill, stroke, strokeWidth }));
        geom = { cx, cy, r };
        drawn = true;
        break;
      }
      case "square": {
        const s = toPixels(dimensions.side ?? 3, Math.min(w, h));
        layer.add(new Konva.Rect({ x: cx - s/2, y: cy - s/2, width: s, height: s, fill, stroke, strokeWidth }));
        geom = { cx, cy, side: s }; // Store the converted pixel value
        drawn = true;
        break;
      }
      case "rectangle": {
        const L = toPixels(dimensions.length ?? 5, w);
        const H = toPixels(dimensions.width ?? 3, h);
        layer.add(new Konva.Rect({ x: cx - L/2, y: cy - H/2, width: L, height: H, fill, stroke, strokeWidth }));
        geom = { cx, cy, length: L, width: H }; // Store converted pixel values
        drawn = true;
        break;
      }
      case "triangle": {
        const base = toPixels(dimensions.base ?? 5, w);
        const ht = toPixels(dimensions.height ?? (base * Math.sqrt(3)) / 2, h);
        const A = { x: cx - base/2, y: cy + ht/3 };
        const B = { x: cx + base/2, y: cy + ht/3 };
        const C = { x: cx, y: cy - 2*ht/3 };
        layer.add(new Konva.Line({ points: [A.x, A.y, B.x, B.y, C.x, C.y], closed: true, fill, stroke, strokeWidth }));
        geom = { A, B, C };
        drawn = true;
        break;
      }
      case "regular_polygon": {
        const R = toPixels(dimensions.side_length ?? 3, Math.min(w, h));
        layer.add(new Konva.RegularPolygon({
          x: cx, y: cy, sides: dimensions.num_sides ?? 6,
          radius: R, fill, stroke, strokeWidth
        }));
        geom = { cx, cy, radius: R, num_sides: dimensions.num_sides ?? 6 };
        drawn = true;
        break;
      }
      default:
        drawn = addExtraShape({ type, dimensions, cx, cy, fill, stroke, strokeWidth, layer });
        if (drawn) {
          // For shapes drawn by addExtraShape, we need to store converted dimensions
          const convertedDims = {};
          if (type === "parallelogram") {
            convertedDims.base = toPixels(dimensions.base ?? 5, w);
            convertedDims.height = toPixels(dimensions.height ?? 3, h);
            convertedDims.slant = toPixels(dimensions.slant ?? 1.5, w);
          } else if (type === "trapezoid") {
            convertedDims.base1 = toPixels(dimensions.base1 ?? 5, w);
            convertedDims.base2 = toPixels(dimensions.base2 ?? 4, w);
            convertedDims.height = toPixels(dimensions.height ?? 3, h);
          }
          geom = { cx, cy, ...convertedDims };
        }
    }

    if (!drawn) throw new Error(`Shape '${type}' not supported`);

    /* optional radius / diameter lines */
    if (type==="circle" && geom.r) {
      const r=geom.r;
      if (params.show_radius_line)
        layer.add(new Konva.Line({points:[cx,cy,cx+r,cy],stroke,strokeWidth}));
      if (params.show_diameter_line)
        layer.add(new Konva.Line({points:[cx-r,cy,cx+r,cy],stroke,strokeWidth}));
    }

    /* labels - pass the geom object which now contains converted pixel dimensions */
    labels.forEach(lab => {
      const p = getLabelPosition(type, lab.position_hint, geom) || { x: cx + 70, y: cy };
      layer.add(new Konva.Text({ 
        ...p, 
        text: lab.text, 
        fontSize: 16,
        fontFamily: FONT_FAMILY, 
        fill: "#212121"
      }));
    });

    if (title) layer.add(new Konva.Text({x:10,y:10,text:title,
                                         fontFamily:FONT_FAMILY,fontSize:18,fontStyle:"bold"}));

    layer.draw();
    await new Promise(r=>requestAnimationFrame(r));
    const url = await getImg(stage, title||type, "geometric_shape", ctx);
    return { success:true, imageUrl:url, caption:title ?? type };

  } catch (e) {
    return { success:false, error:e.message };
  } finally { 
    cleanupStage(stage, container); 
  }
}

/* ─────────── Geometry wrapper helpers (deprecated) ─────────── */
const drawCircle = (p,c) => {
  console.warn("drawCircle is deprecated, use draw_circle instead");
  return draw_circle(p);
};

const drawTriangle = (p,c) => {
  console.warn("drawTriangle is deprecated, use draw_triangle instead");  
  return draw_triangle(p);
};

function drawQuadrilateral(p,c){
  console.warn("drawQuadrilateral is deprecated, use draw_quadrilateral instead");
  return draw_quadrilateral(p);
}

const drawRegularPolygon=(p,c) => {
  console.warn("drawRegularPolygon is deprecated, use draw_polygon_regular instead");
  return draw_polygon_regular(p);
};

/* ───────────────────────── EXECUTOR ───────────────────────── */
export async function executeToolCall(toolName, parameters={}, context={}) {
  try {
    switch(toolName){
      case "plot_graph":            return plotGraph(parameters.expression ?? parameters.function,
                                                     {...parameters,...context});
      /* New geometry functions */
      case "draw_circle":           return draw_circle(parameters);
      case "draw_triangle":         return draw_triangle(parameters);
      case "draw_quadrilateral":    return draw_quadrilateral(parameters);
      case "draw_polygon_regular":  return draw_polygon_regular(parameters);
      case "draw_data_chart":       return draw_data_chart(parameters);
      
      /* Legacy compatibility */
      case "legacy_draw_circle":    return drawCircle(parameters, context);
      case "legacy_draw_triangle":  return drawTriangle(parameters, context);
      case "legacy_draw_quadrilateral": return drawQuadrilateral(parameters, context);
      case "legacy_draw_polygon_regular": return drawRegularPolygon(parameters, context);
      case "legacy_draw_data_chart": return drawDataChart(parameters, context);
      
      default:                      return { success:false, error:`Unknown tool '${toolName}'` };
    }
  } catch(e){ console.error("executeToolCall fatal:",e);
              return { success:false, error:e.message }; }
}

/* ───────────────────── Legacy charts (bar / pie / hist / line) ───────────────────── */
async function _legacyChart(type, points, params, ctx) {
  console.log("Incoming points", points);
  const canvas=document.createElement("canvas");
  canvas.width=params.canvas_width||DEFAULT_CANVAS_WIDTH;
  canvas.height=params.canvas_height||DEFAULT_CANVAS_HEIGHT;
  document.body.appendChild(canvas);
  const c2d=canvas.getContext("2d");

  let chart;
  try{
    const labels=[], values=[];
    if (type==="histogram_simple"){
      const nums=points.map(Number).filter(Number.isFinite);
      const binW=params.histogram_bin_width ??
        (Math.max(...nums)-Math.min(...nums))/Math.ceil(Math.sqrt(nums.length));
      const bins=[];
      nums.forEach(n=>{
        const idx=Math.floor((n-Math.min(...nums))/binW);
        bins[idx]=(bins[idx]||0)+1;
      });
      bins.forEach((v,i)=>{
        labels.push(`${(Math.min(...nums)+i*binW).toFixed(1)}–${(Math.min(...nums)+(i+1)*binW).toFixed(1)}`);
        values.push(v);
      });
    } else if (type==="bar"||type==="pie"){
      points.forEach((p,i)=>{
        if (p && typeof p==="object" && "value" in p){
          labels.push(String(p.label ?? `Item ${i+1}`));
          values.push(Number(p.value));
        } else {
          const arr=Array.isArray(p)?p:[p];
          labels.push(String(arr[0] ?? `Item ${i+1}`));
          values.push(Number(arr[1] ?? arr[0]));
        }
      });
    } else if (type==="line_discrete"){
      points.forEach(p=>{ labels.push(String(p.x)); values.push(p.y); });
    } else return { success:false, error:`Chart type '${type}' not supported` };

    const colours=labels.map((_,i)=>PALETTE[i%PALETTE.length]);
    const maxVal=Math.max(...values,1);
    console.log("Max value", maxVal);
    console.log("Final labels", labels);
    console.log("Final values", values);
    chart=new Chart(c2d,{
      type: type==="line_discrete"?"line" : type==="histogram_simple"?"bar":type,
      data:{ labels,
             datasets:[{ label:params.title||"data", data:values,
                         backgroundColor:type==="line_discrete"?undefined:colours,
                         borderColor:type==="line_discrete"?PALETTE[1]:colours,
                         borderWidth:1, tension:type==="line_discrete"?0.1:undefined,
                         pointRadius:type==="line_discrete"?3:undefined }]},
      options:{ responsive:false,
                plugins:{ title:{display:true,text:params.title||`${type} chart`,
                                 font:{size:18}}, legend:{display:type!=="pie"} },
                scales: type==="pie"?{}:{
                    y:{beginAtZero:true, max:maxVal*1.1},
                    x:{ticks:{autoSkip:false}}
                }}
    });
    await new Promise(r=>requestAnimationFrame(r));
    const url=await getImg(chart,params.title||type,"data_chart",ctx);
    return { success:true, imageUrl:url };

  }catch(e){
    console.error("_legacyChart error",e);
    return { success:false, error:e.message };
  }finally{ cleanupChart(chart,canvas); }
}
